import { DatePipe } from '@angular/common';
import { LocalizationModule } from '@abp/ng.core';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { GoliathCoreEnumerationTypeStore, KanbanCardBodyComponent } from '@digiwall/goliath-core';
import { PlanningDataSource } from '@digiwall/goliath-documents/proxy';
import { GoliathDocumentsEnumerationTypeStore } from '../../../enumeration-types/enumeration-type.store';
import { BasePlanningCardComponent } from '../base-planning-card.component';
import { PlanningDesiredDateComponent } from '../../planning-desired-date.component';
import { ServiceWorkOrderStepPlanningItemFieldsDto } from './service-work-order-step-planning-item-fields';

/**
 * Item card for the ServiceWorkOrderStep planning data source. Rendered by the board through
 * NgComponentOutlet with only the generic item dto; the specifics are read from the typed
 * `displayFields` payload.
 *
 * Layout (inverse of the base ServiceWorkOrder card): the client is the muted reference (top-left) and
 * the step status resolves to the header dot/pill — both via the shared card-body chrome. The
 * work-order number is the emphasised heading, with a right-aligned "Step X" tag on the same row (the
 * step's order within the work order) so a planner can lay the steps out in sequence — ordering is not
 * enforced by the backend.
 */
@Component({
  selector: 'document-planning-service-work-order-step-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe],
  imports: [KanbanCardBodyComponent, LocalizationModule, PlanningDesiredDateComponent],
  template: `
    <core-kanban-card-body
      [reference]="fields().customer"
      [statusEnum]="store.workOrderStatus()"
      [status]="dto().statusId"
      [statusEditable]="statusEditable()"
      [selectableStatuses]="store.workOrderStatus()"
      [statusItemId]="statusItemId()"
    >
      <div class="kanban-card-body__heading d-flex align-items-center gap-2">
        <span>{{ fields().displayNumber }}</span>
        <span class="kanban-card-body__badge align-self-center mt-0 ms-auto">
          {{ 'Doc::ProductionFolderLine:Step' | abpLocalization }} {{ fields().reference }}
        </span>
      </div>

      @if (isVisible('stepName') && fields().stepName; as stepName) {
        <div class="kanban-card-body__line">
          <span class="kanban-card-body__line-label">{{
            'Doc::ProductionFolderLine:Step' | abpLocalization
          }}</span>
          <span class="kanban-card-body__line-value">{{ stepName }}</span>
        </div>
      }
      @if (isVisible('equipment') && fields().equipment; as equipment) {
        <div class="kanban-card-body__line">
          <span class="kanban-card-body__line-label">{{
            'Product::Equipment:Equipment' | abpLocalization
          }}</span>
          <span class="kanban-card-body__line-value">{{ equipment }}</span>
        </div>
      }
      @if (isVisible('worker') && fields().worker; as worker) {
        <div class="kanban-card-body__line">
          <span class="kanban-card-body__line-label">{{
            'Contact::Worker' | abpLocalization
          }}</span>
          <span class="kanban-card-body__line-value">{{ worker }}</span>
        </div>
      }
      @if (isVisible('articleCode') && fields().articleCode; as articleCode) {
        <div class="kanban-card-body__line">
          <span class="kanban-card-body__line-label">{{
            'Doc::Planning:CardField:ArticleCode' | abpLocalization
          }}</span>
          <span class="kanban-card-body__line-value">{{ articleCode }}</span>
        </div>
      }
      @if (isVisible('article') && fields().article; as article) {
        <div class="kanban-card-body__line">
          <span class="kanban-card-body__line-label">{{ 'Doc::Article' | abpLocalization }}</span>
          <span class="kanban-card-body__line-value">{{ article }}</span>
        </div>
      }
      @if (isVisible('articleSerial') && fields().articleSerial; as serial) {
        <div class="kanban-card-body__line">
          <span class="kanban-card-body__line-label">{{
            'Doc::StockDoc:ArticleSerial' | abpLocalization
          }}</span>
          <span class="kanban-card-body__line-value">{{ serial }}</span>
        </div>
      }
      @if (isVisible('responsible') && fields().responsible; as responsible) {
        <div class="kanban-card-body__line">
          <span class="kanban-card-body__line-label">{{ '::Responsible' | abpLocalization }}</span>
          <span class="kanban-card-body__line-value">{{ responsible }}</span>
        </div>
      }
      @if (isVisible('schedule') && scheduleLabel(); as scheduleLabel) {
        <div class="kanban-card-body__line">{{ scheduleLabel }}</div>
      }
      @if ((isVisible('kind') && kind()) || dto().requestedDate) {
        <div class="kanban-card-body__line d-flex align-items-center gap-2">
          @if (isVisible('kind') && kind(); as kind) {
            <span
              class="kanban-card-body__badge align-self-center mt-0"
              [style.color]="kind.textColor || null"
              [style.background]="kind.backgroundColor || null"
            >
              {{ kind.name.current }}
            </span>
          }
          <document-planning-desired-date
            class="ms-auto"
            [date]="dto().requestedDate"
            [critical]="requestedDateCritical()"
          />
        </div>
      }
    </core-kanban-card-body>
  `,
})
export class ServiceWorkOrderStepPlanningCardComponent extends BasePlanningCardComponent<ServiceWorkOrderStepPlanningItemFieldsDto> {
  protected readonly store = inject(GoliathDocumentsEnumerationTypeStore);
  protected readonly coreStore = inject(GoliathCoreEnumerationTypeStore);
  private readonly datePipe = inject(DatePipe);
  protected readonly dataSource = PlanningDataSource.ServiceWorkOrderStep;

  protected readonly kind = computed(() => {
    const kindId = this.fields().kind;
    const workOrderKinds = this.coreStore.serviceWorkOrderKinds();
    return kindId ? workOrderKinds.find(entity => entity.id === kindId) : undefined;
  });

  protected readonly scheduleLabel = computed(() => {
    const fields = this.fields();
    const date = fields.forecastedDate
      ? (this.datePipe.transform(fields.forecastedDate, 'd MMM y') ?? undefined)
      : undefined;
    const duration = fields.plannedDurationHours ? `${fields.plannedDurationHours}h` : undefined;
    return [date, duration].filter(Boolean).join(' · ') || undefined;
  });
}
